import type { FlowSideBasis, FlowSideBasisSelection } from './presets';
import {
  getDefaultFlowSideBasisSelection,
  normalizeFlowSideBasisSelection,
  parseFlowSideBasis,
} from './presets';
import type { Trade } from './types';
import { TradeDirection, TradeSide } from './types';
import { getTradeDirectionFromTradeSide } from './tradeSide';

export const getFlowSideBasisLabel = (flowSideBasis: FlowSideBasisSelection): string => {
  const flowSideSelection = normalizeFlowSideBasisSelection(flowSideBasis);

  if (flowSideSelection.length > 1) {
    return flowSideSelection.map((side) => getFlowSideBasisLabel(side)).join(' + ');
  }

  const activeFlowSideBasis = flowSideSelection[0];

  switch (activeFlowSideBasis) {
    case 'aboveAsk':
      return 'Above Ask';
    case 'bid':
      return 'Bid';
    case 'belowBid':
      return 'Below Bid';
    case 'mid':
      return 'Mid';
    case 'all':
      return 'All';
    case 'ask':
    default:
      return 'Ask';
  }
};

export const getTradeSidesForFlowSideBasis = (flowSideBasis: FlowSideBasisSelection): string[] => {
  const flowSideSelection = normalizeFlowSideBasisSelection(flowSideBasis);

  if (flowSideSelection.length > 1) {
    return Array.from(
      new Set(flowSideSelection.flatMap((side) => getTradeSidesForFlowSideBasis(side))),
    );
  }

  const activeFlowSideBasis = flowSideSelection[0];

  switch (activeFlowSideBasis) {
    case 'aboveAsk':
      return [TradeSide.AboveAsk, TradeDirection.AboveAsk, TradeSide.Ask, TradeDirection.Ask];
    case 'bid':
      return [TradeSide.Bid, TradeDirection.Bid];
    case 'belowBid':
      return [TradeSide.BelowBid, TradeDirection.BelowBid, TradeSide.Bid, TradeDirection.Bid];
    case 'mid':
      return [TradeSide.Mid, TradeDirection.Mid];
    case 'all':
      return [
        TradeSide.AboveAsk,
        TradeDirection.AboveAsk,
        TradeSide.Ask,
        TradeDirection.Ask,
        TradeSide.Mid,
        TradeDirection.Mid,
        TradeSide.Bid,
        TradeDirection.Bid,
        TradeSide.BelowBid,
        TradeDirection.BelowBid,
      ];
    case 'ask':
    default:
      return [TradeSide.Ask, TradeDirection.Ask];
  }
};

export const getFlowSideBasisCacheKey = (flowSideBasis?: FlowSideBasisSelection | null): string =>
  normalizeFlowSideBasisSelection(flowSideBasis).join(',');

// The option feed stores only three trade sides; "Above Ask" / "Below Bid" are
// client-side refinements of the Ask / Bid sides (derived from tradeDirection), not
// distinct column values. So a per-side count only ever needs these three buckets.
export const DB_TRADE_SIDES = [TradeSide.Ask, TradeSide.Mid, TradeSide.Bid] as const;
export type DbTradeSide = (typeof DB_TRADE_SIDES)[number];
export type FlowSideCounts = Record<DbTradeSide, number>;

/**
 * The DB trade sides a flow-side-basis selection resolves to. Reuses
 * getTradeSidesForFlowSideBasis (which expands to every TradeSide/TradeDirection
 * representation) and keeps only the three values that exist in the column. aboveAsk
 * collapses to ASK and belowBid to BID — the same rows the previous `_in` filter
 * matched — so this maps 1:1 onto the old count behavior.
 */
export const getDbTradeSidesForFlowSideBasis = (
  flowSideBasis?: FlowSideBasisSelection | null,
): DbTradeSide[] => {
  const selectedSides = new Set(
    getTradeSidesForFlowSideBasis(flowSideBasis ?? getDefaultFlowSideBasisSelection()),
  );
  return DB_TRADE_SIDES.filter((side) => selectedSides.has(side));
};

/**
 * Collapse a cached per-side count breakdown down to the single total for the
 * requested side selection. This is the server-side "filter in the function" step
 * that lets one cached {ASK, MID, BID} entry answer all seven side combinations.
 */
export const sumFlowSideCounts = (
  counts: FlowSideCounts,
  flowSideBasis?: FlowSideBasisSelection | null,
): number =>
  getDbTradeSidesForFlowSideBasis(flowSideBasis).reduce(
    (total, side) => total + (counts[side] || 0),
    0,
  );

// Order-insensitive equality for the trade-side arrays produced by
// getTradeSidesForFlowSideBasis. Used to skip redundant refetches when the
// resolved side set hasn't actually changed.
export const areTradeSidesEqual = (left: string[], right: string[]): boolean =>
  left.length === right.length && left.every((side) => right.includes(side));

export const getFlowSideBasisFromTrade = (
  trade?: Pick<Trade, 'tradeDirection' | 'tradeSide'> | null,
) => {
  const tradeDirection = trade?.tradeDirection || getTradeDirectionFromTradeSide(trade?.tradeSide);

  if (tradeDirection === TradeDirection.AboveAsk) {
    return 'aboveAsk';
  }

  if (tradeDirection === TradeDirection.BelowBid) {
    return 'belowBid';
  }

  if (tradeDirection === TradeDirection.Bid) {
    return 'bid';
  }

  if (tradeDirection === TradeDirection.Mid) {
    return 'mid';
  }

  return 'ask';
};

export const getFlowSideBasisFromQuery = (value: unknown): FlowSideBasis =>
  parseFlowSideBasis(value) || 'ask';
